import { Component, Input, OnInit, Output, EventEmitter, OnDestroy, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BatchService } from '../../services/batch/batch.service';
import { FileUploadStatus } from '../../services/file-upload/file-upload.service';
import {
  BatchDocumentRequest,
  BulkCreateBatchDocumentRequest,
  CreateBatchRequest
} from '../../models/sdk-interfaces';
import { NgxSpinnerService, NgxSpinnerComponent } from 'ngx-spinner';
import { Observable, Subscription } from 'rxjs';
import { AutoclassifierSdkService } from '../../services/autoclassifier-sdk/autoclassifier-sdk.service';
import { MessageService, PrimeTemplate } from 'primeng/api';
import { AuthService } from '../../services/auth/auth.service';
import { BatchSettingsComponent } from '../batch-settings/batch-settings.component';
import { FileUploaderComponent } from '../file-uploader/file-uploader.component';
import { NgClass } from '@angular/common';
import { Bind } from 'primeng/bind';
import { Button } from 'primeng/button';
import { Toast } from 'primeng/toast';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-batch-creation',
  templateUrl: './batch-creation.component.html',
  styleUrls: ['./batch-creation.component.scss'],
  imports: [
    BatchSettingsComponent,
    FileUploaderComponent,
    NgClass,
    NgxSpinnerComponent,
    Bind,
    Button,
    Toast,
    PrimeTemplate,
    TranslatePipe
  ]
})
export class BatchCreationComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private batchService = inject(BatchService);
  private autoclassifierSdkService = inject(AutoclassifierSdkService);
  private spinner = inject(NgxSpinnerService);
  private messageService = inject(MessageService);
  private authService = inject(AuthService);

  @Input() tenantId!: string;
  @Input() userId!: string;
  @Input() deleteDocumentsEvent = new Observable<void>();
  @Input() areas: any[] = [];

  @Output() cancelBatchCreationEvent = new EventEmitter();
  @Output() hasUploadedFilesEvent = new EventEmitter();
  @Output() finishBatchCreationEvent = new EventEmitter();

  private deleteDocumentsSubscription = new Subscription();

  batchFormGroup!: FormGroup;
  uploadStatuses: FileUploadStatus[] = [];
  isExpanded: boolean = true;
  fileIdsToAssociate: string[] = [];

  /**
   * Initializes the component
   */
  ngOnInit(): void {
    this.batchFormGroup = this.fb.group({
      batchId: [''],
      name: [''],
      department: ['', Validators.required],
      defaultArea: [''],
      defaultDocumentType: ['']
    });

    this.deleteDocumentsSubscription = this.deleteDocumentsEvent.subscribe(() => {
      this.deleteDocuments();
    });

    this.createBatchId();

    this.batchFormGroup.valueChanges.subscribe(() => {
      if (this.fileIdsToAssociate.length === 0 && this.batchFormGroup.touched) {
        this.batchFormGroup.setErrors({ noFiles: true });
      }
    });
  }

  /**
   * Cleans up subscriptions on component destruction
   */
  ngOnDestroy(): void {
    this.deleteDocumentsSubscription.unsubscribe();
  }

  /**
   * Creates a new batch ID
   */
  createBatchId() {
    this.batchService.createBatchId().then(response => {
      this.batchFormGroup.controls['batchId'].setValue(response.batchId);
      this.batchFormGroup.controls['batchId'].disable();
    });
  }

  /**
   * Checks if the form has errors
   * @returns true if the form has errors, false otherwise
   */
  formHasErrors(): boolean {
    const errors = this.batchFormGroup.errors;
    return errors !== null && Object.keys(errors).length > 0;
  }

  /**
   * Checks if the form has a specific error
   * @param error - The name of the error to check for
   * @returns true if the form has the specified error, false otherwise
   */
  checkFormError(error: string): boolean {
    return this.batchFormGroup.getError(error);
  }

  /**
   * Determines if a batch can be created based on upload statuses and form validity
   * @returns true if a batch can be created, false otherwise
   */
  canCreateBatch(): boolean {
    return (
      this.uploadStatuses.some(file => file.status === 'completed') &&
      this.uploadStatuses.every(file => file.status === 'completed' || file.status === 'failed') &&
      this.batchFormGroup.valid
    );
  }

  /**
   * Creates a new batch with the provided data
   */
  createBatch() {
    if (this.fileIdsToAssociate.length > 0 && this.batchFormGroup.valid) {
      const batchData: CreateBatchRequest = {
        ...this.batchFormGroup.value,
        batchId: this.batchFormGroup.controls['batchId'].value,
        status: 'available',
        defaultArea: this.batchFormGroup.controls['defaultArea'].value,
        defaultDocumentType: this.batchFormGroup.controls['defaultDocumentType'].value
      };

      const bulkCreateRequest: BulkCreateBatchDocumentRequest = {
        documents: []
      };

      const uniqueFileIds = [...new Set(this.fileIdsToAssociate)];
      uniqueFileIds.forEach((fileId, index) => {
        const batchDocument: BatchDocumentRequest = {
          documentId: fileId,
          source: 'document',
          documentOrder: index + 1
        };
        bulkCreateRequest.documents.push(batchDocument);
      });

      const totalDocuments = bulkCreateRequest.documents.length;
      const maxDocumentsPerCall = 100;

      if (totalDocuments <= maxDocumentsPerCall) {
        // If the number of documents is less than or equal to 100, create the batch in a single call
        batchData.documents = bulkCreateRequest.documents;
        this.batchService.createBatch(batchData).then(response => {
          this.finishBatchCreation();
        });
      } else {
        // If the number of documents is greater than 100, create the batch in multiple calls
        const initialBatchDocuments = bulkCreateRequest.documents.slice(0, maxDocumentsPerCall);
        batchData.documents = initialBatchDocuments;

        this.batchService.createBatch(batchData).then(() => {
          const remainingDocuments = bulkCreateRequest.documents.slice(maxDocumentsPerCall);
          const batches = this.chunkDocuments(remainingDocuments, maxDocumentsPerCall);

          batches.forEach(batch => {
            const bulkCreateBatchRequest: BulkCreateBatchDocumentRequest = {
              documents: batch
            };
            this.batchService.bulkCreateBatchDocuments(
              batchData.batchId ?? '',
              bulkCreateBatchRequest
            );
          });

          this.finishBatchCreation();
        });
      }
    } else {
      if (!this.batchFormGroup.valid) {
        this.batchFormGroup.markAllAsTouched();
      }
      if (this.fileIdsToAssociate.length === 0) {
        this.batchFormGroup.setErrors({ noFiles: true });
      }
    }
  }

  /**
   * Splits a BatchDocumentRequest array into smaller arrays of a specified size
   * @param array - The array of BatchDocumentRequest objects to split
   * @param size - The maximum number of documents per chunk
   */
  chunkDocuments(array: BatchDocumentRequest[], size: number): BatchDocumentRequest[][] {
    const result = [];
    for (let i = 0; i < array.length; i += size) {
      result.push(array.slice(i, i + size));
    }
    return result;
  }

  /**
   * Emits an event to cancel batch creation
   */
  cancelBatchCreation() {
    this.cancelBatchCreationEvent.emit();
  }

  /**
   * Deletes documents that were uploaded for the batch
   */
  deleteDocuments() {
    const authToken = this.authService.getAuthToken();
    this.fileIdsToAssociate.forEach(fileId => {
      this.autoclassifierSdkService.documentManagementSdk.deleteDocument(
        this.tenantId,
        fileId,
        authToken
      );
    });
  }

  /**
   * Emits an event to indicate that batch creation has finished
   */
  finishBatchCreation() {
    this.finishBatchCreationEvent.emit();
  }

  /**
   * Handles the upload status emitted from the FileUploaderComponent
   * @param status - The current status of the file uploads
   */
  onUploadStatusUpdate(status: FileUploadStatus[]): void {
    if (
      this.checkFormError('noFiles') &&
      status.some(file => file.status === 'completed') &&
      this.batchFormGroup.errors
    ) {
      this.batchFormGroup.setErrors(null);
    }

    if (status.every(file => file.status !== 'uploading')) {
      this.uploadStatuses = [...this.uploadStatuses, ...status];
    }
  }

  /**
   * Toggles the expanded state of the upload status section
   */
  toggleUploadStatus(): void {
    this.isExpanded = !this.isExpanded;
  }

  /**
   * Returns the number of files that have been successfully uploaded
   */
  uploadedCount(): number {
    return this.uploadStatuses.filter(file => file.status === 'completed').length;
  }

  /**
   * Associates uploaded file IDs with the batch
   * @param fileIds - Array of document IDs to associate with the batch
   */
  setFileIdsToAssociate(fileIds: string[]) {
    this.hasUploadedFilesEvent.emit();
    fileIds.forEach(fileId => {
      this.fileIdsToAssociate.push(fileId);
    });
  }

  /**
   * Shows an error toast message.
   * @param {string} toastMessage - The error message to display.
   * @param {string} [toastData] - Additional data to display in the toast.
   */
  showErrorToast(toastMessage: string, toastData?: string): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: toastMessage,
      key: 'errorToast',
      data: toastData ? toastData : '',
      life: 10000
    });
  }

  /**
   * Handles errors during document upload
   * @param {string} error - The error message to display
   */
  handleDocumentUploadError(error: string) {
    this.showErrorToast(error);
  }
}
